import { FullStory, init, isInitialized } from '@fullstory/browser';

import { FULLSTORY_ORG_ID, RUNTIME_ENV } from '@frontend/envConfig';
import type { User } from '@frontend/types';

// Whether Fullstory is configured for this environment. The org id is supplied
// per-environment (localdev / integration / production) via FULLSTORY_ORG_ID, and
// every session is tagged with `environment` so data stays filterable. An empty org
// id makes the whole integration a no-op, keeping the app safe before a key is
// provisioned (and letting any environment opt out by clearing the value).
const isEnabled = (): boolean => typeof window !== 'undefined' && Boolean(FULLSTORY_ORG_ID);

// Initialize Fullstory as early as possible (on module import, like utils/sentry.ts)
// so the full session — including the pre-auth login flow — is captured. The
// isInitialized() check guards against double-init across hot reloads. Subsequent
// identify / setProperties calls are queued by Fullstory until the recording script
// finishes loading, so they only need to know the integration is enabled, not that
// the script has finished booting.
if (isEnabled() && !isInitialized()) {
  init({ orgId: FULLSTORY_ORG_ID });
}

/**
 * Associate the current Fullstory session with the authenticated user and their
 * customer / instance context. Safe to call repeatedly; Fullstory merges properties.
 */
export const identifyFullstoryUser = (user: User): void => {
  if (!isEnabled()) {
    return;
  }
  const { instance } = user;
  FullStory('setIdentity', {
    uid: user.id,
    properties: {
      displayName: [user.firstname, user.lastname].filter(Boolean).join(' '),
      email: user.email,
      environment: RUNTIME_ENV,
      customerId: instance.customer_id,
      instanceId: instance.instance_id,
      productId: instance.product_id,
      productSku: instance.product_sku,
      homeOrganizationId: user.home_organization_id,
      isOptiUser: user.email.endsWith('@optimizely.com'),
      ...(instance.hosted_region ? { hostedRegion: instance.hosted_region } : {}),
    },
  });
};

/**
 * Attach page-level properties (e.g. instance id, app section) to the current
 * Fullstory page. Fullstory already captures the URL automatically; this enriches
 * the page with context that is otherwise embedded in the path. Properties are
 * merged until the URL host/path changes, at which point Fullstory starts a new page.
 */
export const setFullstoryPageProperties = (
  properties: Record<string, string | number | boolean>
): void => {
  if (!isEnabled()) {
    return;
  }
  FullStory('setProperties', { type: 'page', properties });
};

/**
 * The current Fullstory session URL deep-linked to the moment of call, or `null`
 * when Fullstory is disabled, the recording script has not booted yet, or the
 * ingest is being dropped server-side (e.g. the domain is not on the org's
 * capture allowlist). Attached to Sentry events so incident debugging can jump
 * from an error straight to the replay.
 */
export const getFullstorySessionURL = (): string | null => {
  if (!isEnabled()) {
    return null;
  }
  return FullStory('getSession', { format: 'url.now' }) ?? null;
};

/**
 * Emit a named custom event into the current Fullstory session. Shows up as a
 * timeline marker on the replay and as a searchable event in analytics.
 * Used to drop a `Sentry Error` marker at the moment a Sentry event fires, so
 * a specific error can be located inside a replay and vice versa.
 */
export const trackFullstoryEvent = (
  name: string,
  properties: Record<string, string | number | boolean>
): void => {
  if (!isEnabled()) {
    return;
  }
  FullStory('trackEvent', { name, properties });
};
